import { type Control, Controller, type FieldErrors, type FieldValues } from 'react-hook-form';
import { cn } from '../../lib/utils';
import type { FormFieldSchema } from '../types';
import { FieldRenderer } from './field-renderer';

export interface FormRendererProps {
  fields: FormFieldSchema[];
  mode: 'view' | 'edit';
  control?: Control<FieldValues>; // React Hook Form control
  errors?: FieldErrors<FieldValues>;
  values?: Record<string, unknown>; // Used in view mode or if control is not provided
  onChange?: (fieldId: string, value: unknown) => void; // Optional fallback
  onUploadFile?: (file: File, category?: string) => Promise<number>;
  onGetFile?: (fileId: number) => Promise<{ fileName: string; fileType: string; viewUrl?: string }>;
  onRenameFile?: (fileId: number, newName: string) => Promise<void>;
  className?: string;
}

export function FormRenderer({
  fields,
  mode,
  control,
  errors,
  values = {},
  onChange,
  onUploadFile,
  onGetFile,
  onRenameFile,
  className,
}: FormRendererProps) {
  if (fields.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg bg-muted/30">
        <p className="text-muted-foreground text-sm">No custom fields defined.</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 w-full', className)}>
      {fields.map((field) => {
        // Use field.name as the primary key for form state, fallback to id
        const formKey = field.name || field.id;
        if (!formKey) return null;

        if (mode === 'edit' && control) {
          return (
            <Controller
              key={field.id}
              name={formKey}
              control={control}
              render={({ field: { onChange: rhfOnChange, value }, fieldState: { error } }) => (
                <FieldRenderer
                  field={field}
                  mode="edit"
                  value={value}
                  onChange={(val) => {
                    rhfOnChange(val);
                    onChange?.(formKey, val);
                  }}
                  onUploadFile={onUploadFile}
                  onGetFile={onGetFile}
                  onRenameFile={onRenameFile}
                  error={error?.message}
                />
              )}
            />
          );
        }

        return (
          <FieldRenderer
            key={field.id}
            field={field}
            mode={mode}
            value={values[formKey]}
            onChange={(val) => onChange?.(formKey, val)}
            onUploadFile={onUploadFile}
            onGetFile={onGetFile}
            onRenameFile={onRenameFile}
            error={errors?.[formKey]?.message as string | undefined}
          />
        );
      })}
    </div>
  );
}
